import { getCsrfToken } from "utils/csrf";

const API_ENDPOINT = "/file-upload";

/**
 * Fetch file templates for a specific project
 */
export async function fetchFileTemplates(projectId) {
  const params = projectId ? `?project_id=${encodeURIComponent(projectId)}` : "";
  const response = await fetch(`${API_ENDPOINT}/file-templates/${params}`, {
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  });

  // Check content type first
  const contentType = response.headers.get("content-type");
  if (contentType && !contentType.includes("application/json")) {
    // If we got HTML, it's likely an error page - try to get the status text
    const text = await response.text();
    throw new Error(
      `Server returned HTML (${response.status} ${response.statusText}). Check that the API endpoint is correct.`,
    );
  }

  if (!response.ok) {
    let errorMessage = `Failed to fetch file templates (${response.status})`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Get a signed GCS URL for a direct browser upload.
 * Returns { file_id, uri, upload_url, upload_headers }.
 */
async function getSignedUploadUrl(fileTemplateId, fileName) {
  const response = await fetch(`${API_ENDPOINT}/files/signed-url/`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
      Accept: "application/json",
    },
    body: JSON.stringify({ file_template_id: fileTemplateId, file_name: fileName }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to get upload URL (${response.status})`);
  }

  return response.json();
}

/**
 * Upload a single file to a file template via the two-phase signed-URL flow:
 * 1. Get a signed upload URL from the backend.
 * 2. PUT the file bytes directly to GCS.
 * 3. Confirm with the backend, which validates and creates the file record.
 * Returns the backend report { successes, failures }.
 */
export async function uploadFile(fileTemplateId, file) {
  const { file_id, uri, upload_url, upload_headers } = await getSignedUploadUrl(
    fileTemplateId,
    file.name,
  );

  const putResponse = await fetch(upload_url, {
    method: "PUT",
    body: file,
    headers: upload_headers,
  });

  if (!putResponse.ok) {
    throw new Error(`Failed to upload file to storage (${putResponse.status})`);
  }

  const response = await fetch(`${API_ENDPOINT}/files/`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
      Accept: "application/json",
    },
    body: JSON.stringify({
      file_template_id: fileTemplateId,
      file_id,
      uri,
      file_name: file.name,
    }),
  });

  const contentType = response.headers.get("content-type");
  if (contentType && !contentType.includes("application/json")) {
    throw new Error(
      `Server returned HTML (${response.status} ${response.statusText}). Check that the API endpoint is correct.`,
    );
  }

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error(`Server error: ${response.status} ${response.statusText}`);
  }

  if (!response.ok && data.error) {
    throw new Error(data.error);
  }

  return data;
}

/**
 * Delete a file
 */
export async function deleteFile(fileTemplateId, fileId) {
  const response = await fetch(
    `${API_ENDPOINT}/files/${fileTemplateId}/${fileId}/`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": getCsrfToken(),
        Accept: "application/json",
      },
      credentials: "include",
    },
  );

  // Check content type first
  const contentType = response.headers.get("content-type");
  if (contentType && !contentType.includes("application/json")) {
    throw new Error(
      `Server returned HTML (${response.status} ${response.statusText}). Check that the API endpoint is correct.`,
    );
  }

  if (!response.ok) {
    let errorMessage = `Failed to delete file (${response.status})`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }
}

/**
 * Download a file by getting presigned URL and triggering download
 */
export async function downloadFile(fileTemplateId, fileId, fileName) {
  const response = await fetch(
    `${API_ENDPOINT}/files/${fileTemplateId}/${fileId}/`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": getCsrfToken(),
        Accept: "application/json",
      },
      credentials: "include",
    },
  );

  // Check content type first
  const contentType = response.headers.get("content-type");
  if (contentType && !contentType.includes("application/json")) {
    throw new Error(
      `Server returned HTML (${response.status} ${response.statusText}). Check that the API endpoint is correct.`,
    );
  }

  if (!response.ok) {
    let errorMessage = `Failed to download file (${response.status})`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();

  // Create a temporary link and trigger download
  if (data.presigned_url) {
    const link = document.createElement("a");
    link.href = data.presigned_url;
    link.download = fileName || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    throw new Error("No presigned URL returned from server");
  }
}
